import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, videos } from '@/mocks/data';

function formatViews(n: number): string {
  if (n >= 10000000) return `${(n / 10000000).toFixed(1)}千万`;
  if (n >= 10000) return `${(n / 10000).toFixed(0)}万`;
  return n.toString();
}

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryVideos = videos.filter((v) => v.category === slug);

  return (
    <main className="min-h-screen bg-background-50">
      <Navbar />

      <section className="pt-20 md:pt-24 pb-8 bg-background-100/70">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-4" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 cursor-pointer no-underline whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            {currentCategory && (
              <span className="text-foreground-800 font-medium">{currentCategory.name}</span>
            )}
          </nav>

          {currentCategory && (
            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-2">
                {currentCategory.name}
              </h1>
              <p className="text-sm text-foreground-500 max-w-2xl">
                {currentCategory.description}
              </p>
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm whitespace-nowrap cursor-pointer no-underline transition-all duration-200 ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-50 text-foreground-600 border border-background-200/70 hover:border-primary-300'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14">
        {categoryVideos.length > 0 ? (
          <>
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-foreground-500">
                共 <strong className="text-foreground-800">{categoryVideos.length}</strong> 部视频
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {categoryVideos.map((video) => (
                <Link
                  key={video.id}
                  to={`/article/${video.id}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer no-underline hover:translate-y-[-2px]"
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={video.coverImage}
                      alt={video.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                      title={`${video.title} - 芭乐视频`}
                    />
                    <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-md">
                      {formatViews(video.views)}次播放
                    </div>
                  </div>
                  <div className="p-3 md:p-4">
                    <h3 className="font-label text-sm md:text-base text-foreground-900 font-medium line-clamp-1 mb-1">
                      {video.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2 mb-2 leading-relaxed">
                      {video.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-accent-600 flex items-center gap-0.5">
                        <i className="ri-star-fill text-xs"></i> {video.rating}
                      </span>
                      <span className="text-xs text-foreground-400">{video.platform}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-inbox-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500">该分类暂无视频资源</p>
            <Link
              to="/"
              className="inline-block mt-4 text-sm text-primary-600 hover:text-primary-700 cursor-pointer no-underline"
            >
              返回首页
            </Link>
          </div>
        )}
      </section>

      <Footer />
    </main>
  );
}